import { MavMessage } from "./MavMessage";
import { MavRegistry } from "./MavRegistry";
import { MavlinkCRC } from "./MavlinkCRC";

type Callback<K extends MavMessage> = (payload: ReturnType<typeof MavRegistry[K]["parse"]>) => void;

function calcMavlinkV2Crc(bytes: Uint8Array, msgid: number): number {
  const crcBase = MavlinkCRC.crcCalculate(bytes, bytes.length - 2);

  const extra = MAVLINK_EXTRA_CRC[msgid] ?? 0;

  return MavlinkCRC.crcAccumulate(extra, crcBase);
}

const MAVLINK_EXTRA_CRC: Record<number, number> = {
  0: 50,   // HEARTBEAT
  1: 124,  // SYS_STATUS
  24: 23,  // GPS_RAW_INT
  30: 39,  // ATTITUDE
  147: 154,// BATTERY_STATUS
  74: 20   // VFR_HUD
};

const MAVLINK_PAYLOAD: Record<number, number> = {
  0: 9,   // HEARTBEAT
  1: 31,  // SYS_STATUS
  24: 52,  // GPS_RAW_INT
  30: 28,  // ATTITUDE
  147: 49,// BATTERY_STATUS
  74: 20   // VFR_HUD
};

export class MavlinkParser {
  private listeners = new Map<MavMessage, Callback<any>[]>();

  onMessage<K extends MavMessage>(type: K, cb: Callback<K>) {
    if (!this.listeners.has(type)) this.listeners.set(type, []);
    this.listeners.get(type)!.push(cb);
  }

  private emit<K extends MavMessage>(type: K, payload: ReturnType<typeof MavRegistry[K]["parse"]>) {
    const list = this.listeners.get(type);
    if (list) for (const fn of list) fn(payload);
  }

  parseMavlinkV2(bytes: Uint8Array) {
    if (bytes[0] !== 0xFD) return;
    
    const len = bytes[1];
    const msgid = bytes[7] | (bytes[8] << 8) | (bytes[9] << 16);
    const payloadStart = 10;
    const payloadEnd = payloadStart + len;
    let payload = bytes.slice(payloadStart, payloadEnd);
    //const payload = bytes.slice(10, 10 + len);

    const crcLow = bytes[payloadEnd];
    const crcHigh = bytes[payloadEnd + 1];
    //const crcPacket = (crcHigh << 8) | crcLow;
    const crcPacket = (crcLow | (crcHigh << 8)) & 0xFFFF;

    const signature = bytes.length > payloadEnd + 2
      ? bytes.slice(payloadEnd + 2)
      : null;

    const crcCalculated = calcMavlinkV2Crc(bytes, msgid);
    const crcOk = crcCalculated === crcPacket;
    
    if (!crcOk) return;

    const handler = MavRegistry[msgid as MavMessage];
    if (handler) {
      const len = MAVLINK_PAYLOAD[msgid];
      if(payload.length < len) {
        const padded = new Uint8Array(len);
        padded.set(payload, 0);
        payload = padded; 
      }

      const parsed = handler.parse(payload);
      this.emit(msgid as MavMessage, parsed);
    } else {
      console.warn(`Unknown msgid: ${msgid}`);
    }
  }
}

export const mavlink = new MavlinkParser();
